import { Box, BoxProps, styled, Typography } from "@mui/material";
import React from "react";

/**
 * Figma "location-info" chip fills: the default teal, the yellow warning
 * (e.g. a canceled product), `muted` — no fill, secondary text — for
 * inactive states, and the two quiet fills the location workspace draws its
 * IP counts with: `neutral` (the page ground, for a chip sitting on paper)
 * and `paper` (for a chip sitting on the ground-coloured panel).
 */
type LocationInfoChipVariant =
  | "default"
  | "warning"
  | "muted"
  | "neutral"
  | "paper";

const Count = styled(Box, {
  shouldForwardProp: (prop) => prop !== "variant",
})<{ variant: LocationInfoChipVariant }>(({ theme, variant }) => ({
  color: "black",
  fontWeight: 500,
  background: theme.palette.locationInfo.background,
  borderRadius: 50,
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  ...(variant === "warning" && {
    background: theme.palette.locationInfo.warningBackground,
  }),
  ...(variant === "muted" && {
    background: "transparent",
    color: theme.palette.text.secondary,
    fontWeight: 400,
  }),
  ...((variant === "neutral" || variant === "paper") && {
    background:
      variant === "neutral"
        ? theme.palette.background.default
        : theme.palette.background.paper,
    color: theme.palette.text.secondary,
    fontWeight: 400,
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  }),
}));

type Props = BoxProps & { variant?: LocationInfoChipVariant };

export default function LocationInfoChip({
  children,
  variant = "default",
  ...props
}: Props) {
  return (
    <Count
      {...props}
      variant={variant}
      display="inline-flex"
      alignItems="center"
      flexDirection="row"
      flexWrap="nowrap"
      gap={1}
    >
      {typeof children === "string" ? (
        <Typography fontSize="11px" lineHeight="18px" fontWeight="inherit">
          {children}
        </Typography>
      ) : (
        children
      )}
    </Count>
  );
}
